//! Holds a column header within a table.
//!
//! Each header carries an always-visible dropdown button (Excel AutoFilter)
//! at its right edge that opens the column's sort-and-filter menu. The button
//! shows a caret by default, the sort direction arrow when the column is
//! sorted, and a funnel when a filter is active. A stale sort/filter (source
//! data changed) tints the button red.

import type { Table } from '@/app/gridGL/cells/tables/Table';
import type { TablePointerDownResult } from '@/app/gridGL/cells/tables/Tables';
import { intersects } from '@/app/gridGL/helpers/intersects';
import { getCSSVariableTint } from '@/app/helpers/convertColor';
import type { DataTableSort } from '@/app/quadratic-core-types';
import { colors } from '@/app/theme/colors';
import { LINE_HEIGHT, OPEN_SANS_FIX } from '@/app/web-workers/renderWebWorker/worker/cellsLabel/CellLabel';
import { DEFAULT_FONT_SIZE, SORT_BUTTON_PADDING, SORT_BUTTON_RADIUS } from '@/shared/constants/gridConstants';
import type { Point } from 'pixi.js';
import { BitmapText, Container, Graphics, Rectangle, Sprite, Texture } from 'pixi.js';

// Side of the square dropdown button (matches the pivot field button).
const BUTTON_SIZE = 13;

// Gap between the button and the right edge of the header.
const BUTTON_MARGIN = 2;

// Half-width of the caret inside the button.
const CARET_SIZE = 3;

// Size of the sort-direction arrow inside the button.
const SORT_ICON_SIZE = 9;

const BUTTON_DIRTY_TINT = 0xff0000;

export class TableColumnHeader extends Container {
  private table: Table;
  index: number;

  private columnName: BitmapText;

  /** World x where the dropdown button's hit region starts. */
  private buttonStart = 0;

  columnHeaderBounds: Rectangle;
  w: number;
  h: number;

  tableCursor: string | undefined;

  constructor(options: {
    table: Table;
    index: number;
    x: number;
    width: number;
    height: number;
    name: string;
    sort?: DataTableSort;
    filtered: boolean;
    dirty: boolean;
    columnY: number;
  }) {
    super();
    const { table, index, x, width, height, name, sort, filtered, dirty, columnY } = options;
    this.table = table;
    this.index = index;
    this.columnHeaderBounds = new Rectangle(table.tableBounds.x + x, table.tableBounds.y + columnY, width, height);
    this.w = width;
    this.h = height;
    this.position.set(x, 0);

    this.columnName = this.addChild(
      new BitmapText(name, {
        fontName: 'OpenSans-Bold',
        fontSize: DEFAULT_FONT_SIZE,
        tint: colors.cellFontColor,
      })
    );
    this.clipName(name, width);
    this.drawDropdownButton(width, height, sort, filtered, dirty);

    // Calculate available space for vertical positioning (use LINE_HEIGHT like CellLabel)
    const textHeight = LINE_HEIGHT;
    const availableSpace = this.h - textHeight;

    // Calculate vertical position
    const yPos = Math.max(0, availableSpace / 2);

    this.columnName.anchor.set(0, 0);
    this.columnName.position.set(OPEN_SANS_FIX.x, OPEN_SANS_FIX.y + yPos);
  }

  // tests the width of the text and clips it if it is too wide
  private clipName = (name: string, width: number) => {
    let clippedName = name;
    while (clippedName.length > 0 && this.columnName.width + SORT_BUTTON_RADIUS * 2 + SORT_BUTTON_PADDING > width) {
      clippedName = clippedName.slice(0, -1);
      this.columnName.text = clippedName + '…';
    }
  };

  private drawDropdownButton = (
    width: number,
    height: number,
    sort: DataTableSort | undefined,
    filtered: boolean,
    dirty: boolean
  ) => {
    const size = Math.min(BUTTON_SIZE, height - 2);
    const left = width - size - BUTTON_MARGIN;
    const top = (height - size) / 2;
    this.buttonStart = this.columnHeaderBounds.right - size - BUTTON_MARGIN * 2;

    const border = getCSSVariableTint('border');
    const background = getCSSVariableTint('background');
    const icon = dirty
      ? BUTTON_DIRTY_TINT
      : filtered
        ? getCSSVariableTint('primary')
        : getCSSVariableTint('muted-foreground');

    const button = this.addChild(new Graphics());
    button.lineStyle(1, border, 1);
    button.beginFill(background);
    button.drawRoundedRect(left, top, size, size, 2);
    button.endFill();
    button.lineStyle();

    const centerX = left + size / 2;
    const centerY = top + size / 2;

    if (filtered) {
      // funnel: trapezoid mouth narrowing into a stem
      button.beginFill(icon);
      button.drawPolygon([
        centerX - 4,
        centerY - 4,
        centerX + 4,
        centerY - 4,
        centerX + 1,
        centerY - 0.5,
        centerX + 1,
        centerY + 4,
        centerX - 1,
        centerY + 4,
        centerX - 1,
        centerY - 0.5,
      ]);
      button.endFill();
    } else if (sort && sort.direction !== 'None') {
      const sortIcon = this.addChild(
        new Sprite(Texture.from(sort.direction === 'Descending' ? 'sort-descending' : 'sort-ascending'))
      );
      sortIcon.anchor.set(0.5);
      sortIcon.position.set(centerX, centerY);
      sortIcon.width = SORT_ICON_SIZE;
      sortIcon.scale.y = sortIcon.scale.x;
      if (dirty) {
        sortIcon.tint = BUTTON_DIRTY_TINT;
      }
    } else {
      button.beginFill(icon);
      button.drawPolygon([
        centerX - CARET_SIZE,
        centerY - CARET_SIZE / 2,
        centerX + CARET_SIZE,
        centerY - CARET_SIZE / 2,
        centerX,
        centerY + CARET_SIZE,
      ]);
      button.endFill();
    }
  };

  pointerMove = (world: Point): boolean => {
    if (intersects.rectanglePoint(this.columnHeaderBounds, world)) {
      this.tableCursor = world.x > this.buttonStart ? 'pointer' : undefined;
      return true;
    }
    this.tableCursor = undefined;
    return false;
  };

  pointerDown = (world: Point, isRightClick: boolean): TablePointerDownResult | undefined => {
    if (intersects.rectanglePoint(this.columnHeaderBounds, world)) {
      // Right-click on the dropdown button must not open the filter menu; it
      // should fall through to the column header's right-click context menu.
      if (world.x > this.buttonStart && !isRightClick) {
        return { table: this.table.codeCell, type: 'column-filter', column: this.index };
      } else {
        return { table: this.table.codeCell, type: 'column-name', column: this.index };
      }
    }
  };

  toHoverGrid = (y: number) => {
    this.columnHeaderBounds.y = y;
  };
}
